import dayjs from "dayjs"
import {
  setError,
  startLoading,
  stopLoading,
  setSuccess,
  clearNotifications,
  setWarning,
} from "./components/Notification"
import { DATETIME_FORMAT, EMPTY_GUID, DATE_FORMAT } from "./utils/constants"
import { ensureTimeEntryExtensions, ensureTransportEntryExtensions } from "./utils/timeEntryExtensions"

const listEntitiesAsync = async (fetchAuthAsync, apiEntity, filter) => {
  let result = null
  if (typeof fetchAuthAsync !== "function") {
    console.error("listEntitiesAsync fetchAuthAsync should be a function")
    return result
  }
  if (!apiEntity || typeof apiEntity !== "string") {
    console.error("listEntitiesAsync apiEntity should be string")
    return result
  }
  startLoading(`listEntitiesAsync ${apiEntity}${filter}`)
  try {
    let url = `${AZURE_URI}/${apiEntity}`
    if (filter?.length && filter.startsWith("?")) {
      url += filter
    }
    const raw = await fetchAuthAsync(url, "GET")
    if (!Array.isArray(raw.result)) {
      throw raw
    }
    result = raw.result
  } catch (e) {
    setError(e)
  }
  stopLoading(`listEntitiesAsync ${apiEntity}${filter}`)
  return result
}

const listPagedEntitesAsync = async (fetchAuthAsync, apiEntity, paging, statuses, filter) => {
  let result = {
    entities: [],
    numberOfRecords: 0,
  }
  if (typeof fetchAuthAsync !== "function") {
    console.error("listPagedEntitesAsync fetchAuthAsync should be a function")
    return result
  }
  if (!apiEntity || typeof apiEntity !== "string") {
    console.error("listPagedEntitesAsync apiEntity should be string")
    return result
  }
  if (typeof statuses !== "string" || statuses.length < 1) {
    console.warn("no statuses")
    return result
  }
  if (typeof filter !== "string") {
    console.error("filter should be string")
    return result
  }
  if (!paging.pageNum || !paging.itemsPerPage) {
    console.error("paging missing required.")
    return result
  }
  startLoading(`listPagedEntitesAsync ${apiEntity} ${statuses} ${filter}`)
  try {
    let url = `${AZURE_URI}/${apiEntity}`
    if (statuses?.length) {
      url += "/" + statuses
    }
    url += `?pageNum=${paging.pageNum}&itemsPerPage=${paging.itemsPerPage}`
    url += filter
    const raw = await fetchAuthAsync(url, "GET")
    if (!raw.itemsPerPage) {
      throw raw
    }
    result.entities = raw.result
    result.numberOfRecords = raw.numberOfRecords
  } catch (e) {
    setError(e)
  }
  stopLoading(`listPagedEntitesAsync ${apiEntity} ${statuses} ${filter}`)
  return result
}

const getEntityAsync = async (fetchAuthAsync, apiEntity, id, extraParams = "") => {
  let result = null
  if (typeof fetchAuthAsync !== "function") {
    console.error("getEntityAsync fetchAuthAsync should be a function")
    return result
  }
  if (!apiEntity || typeof apiEntity !== "string") {
    console.error("getEntityAsync apiEntity should be string")
    return result
  }
  if (id?.length !== 36 && id !== "new") {
    console.error(`getEntityAsync ${apiEntity}: id should be GUID`)
    return result
  }
  // the zero guid is "nothing picked" (and IS 36 chars) - there is nothing to load
  if (id === EMPTY_GUID) {
    return result
  }
  startLoading(`getEntityAsync ${apiEntity}`)
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/${apiEntity}/${id}${extraParams}`, "GET")
    if (raw?.status !== "ok") {
      throw raw
    }
    result = raw.result
  } catch (e) {
    setError(e)
  }
  stopLoading(`getEntityAsync ${apiEntity}`)
  return result
}

const getWorkOrderAsync = async (fetchAuthAsync, workOrderType, workOrderId) => {
  if (workOrderType?.length < 5) {
    console.error("getWorkOrderAsync: workOrderType missing")
    return null
  }
  // the order types come in batches of dayjs-backed dates the form inputs bind to
  const result = await getEntityAsync(fetchAuthAsync, `WorkOrder/${workOrderType}`, workOrderId)
  if (!result) {
    return null
  }

  result.dueDate = result.dueDate ? dayjs(result.dueDate).format("YYYY-MM-DD") : null
  if (result.dateSubmitted) {
    Object.defineProperty(result, "dateSubmittedObject", {
      get: function () {
        const d = dayjs.utc(this.dateSubmitted)
        return !d.isValid() || d.year() < 2000 ? undefined : d
      },
    })
    Object.defineProperty(result, "dateSubmittedForInput", {
      get: function () {
        return this.dateSubmittedObject?.local()?.format(DATETIME_FORMAT)
      },
      set: function (v) {
        this.dateSubmitted = v
      },
    })
  }
  if (result.fireWatch) {
    // no UTC conversion for these dates - always local time!
    Object.defineProperty(result.fireWatch, "validFromForInput", {
      get: function () {
        const d = dayjs.utc(this.validFrom)
        return !d.isValid() || d.year() < 2000 ? undefined : d.format(DATETIME_FORMAT)
      },
      set: function (v) {
        this.validFrom = v || null
      },
    })
    Object.defineProperty(result.fireWatch, "validToForInput", {
      get: function () {
        const d = dayjs.utc(this.validTo)
        return !d.isValid() || d.year() < 2000 ? undefined : d.format(DATETIME_FORMAT)
      },
      set: function (v) {
        this.validTo = v || null
      },
    })
  }
  return result
}

const patchWorkOrderAsync = async (fetchAuthAsync, req, workOrderType, workOrderId) => {
  if (typeof fetchAuthAsync !== "function") {
    console.error("patchWorkOrderAsync fetchAuthAsync should be a function")
    return false
  }
  startLoading("pathcWorkOrderAsync")
  try {
    clearNotifications()
    const raw = await fetchAuthAsync(`${AZURE_URI}/WorkOrder/${workOrderType}/${workOrderId}`, "PATCH", req)
    if (raw?.status !== "ok") {
      throw raw
    }
    stopLoading("pathcWorkOrderAsync")
    return true
  } catch (e) {
    setError(e)
  }
  stopLoading("pathcWorkOrderAsync")
  return false
}

const getFacilityAsync = async (fetchAuthAsync, id, populateDetails = false) => {
  // date-only helpers for the form inputs
  const result = await getEntityAsync(fetchAuthAsync, "Facility", id, `?populateDetails=${populateDetails}`)
  if (!result) {
    return null
  }

  Object.defineProperty(result, "dateLastRegularServiceObject", {
    get: function () {
      const d = dayjs(this.dateLastRegularService)
      return !d.isValid() || d.year() < 2000 ? undefined : d
    },
  })
  Object.defineProperty(result, "dateLastThermometerCalibrationObject", {
    get: function () {
      const d = dayjs(this.dateLastThermometerCalibration)
      return !d.isValid() || d.year() < 2000 ? undefined : d
    },
  })
  Object.defineProperty(result, "dateLastLeakageCheckObject", {
    get: function () {
      const d = dayjs(this.dateLastLeakageCheck)
      return !d.isValid() || d.year() < 2000 ? undefined : d
    },
  })
  return result
}

const deleteFacilityContactAsync = async (fetchAuthAsync, fc) => {
  let result = null
  if (typeof fetchAuthAsync !== "function") {
    console.error("deleteFacilityContactAsync fetchAuthAsync should be a function")
    return result
  }
  if (!fc) {
    return
  }
  startLoading("deleteFacilityContactAsync")
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/FacilitiesContacts/${fc.id}`, "DELETE", fc)
    if (raw?.status !== "ok") {
      throw raw
    }
    result = raw.result
  } catch (e) {
    setError(e)
  }
  stopLoading("deleteFacilityContactAsync")
  return result
}

// The search query belongs here, not in the callers: four screens search devices (device number
// screen, device card, barcode select, devices list) and they must not drift apart.
const searchDevicesAsync = async (fetchAuthAsync, q) => {
  return await listEntitiesAsync(fetchAuthAsync, "Devices", `?isActive=true&q=${encodeURIComponent(q)}&limit=10`)
}

// same for materials: three callers, and `searchby` is the field being searched (q or acCode)
const searchMaterialsAsync = async (fetchAuthAsync, searchby, q) => {
  return await listEntitiesAsync(
    fetchAuthAsync,
    "Materials",
    `?variant=Full&${searchby}=${encodeURIComponent(q)}&isActive=true`,
  )
}

const saveMaterialsAsync = async (fetchAuthAsync, materials, materialRelation, relationId) => {
  let result = false
  if (typeof fetchAuthAsync !== "function") {
    console.error("saveMaterialsAsync fetchAuthAsync should be a function")
    return result
  }
  if (relationId?.length !== 36) {
    console.error("relationId should be GUID")
    return result
  }
  if (materialRelation?.length < 5) {
    console.error("materialRelation missing")
    return result
  }
  if (!Array.isArray(materials)) {
    console.error("materials not array")
    return result
  }
  materials = materials.filter((m) => m.materialID && !isNaN(m.quantity))
  if (materials.length === 0) {
    setWarning("Količina je povsod 0, zato ne shranjujem.")
    return true
  }
  startLoading("saveMaterialsAsync")
  try {
    let url = `${AZURE_URI}/Materials/${materialRelation}/${relationId}`
    const raw = await fetchAuthAsync(url, "POST", materials)
    if (raw?.status !== "ok" && raw?.status !== "warn") {
      throw raw
    }
    result = raw?.affectedRows > 0
  } catch (e) {
    setError(e)
  }
  stopLoading("saveMaterialsAsync")
  return result
}

const getLeakageCheckFormAsync = async (fetchAuthAsync, id, isNew, workOrderId) => {
  const result = await getEntityAsync(fetchAuthAsync, "LeakageCheckForm", id)
  if (!result) {
    return null
  }
  if (isNew) {
    result.serviceOrderID = workOrderId
  } else if (result.serviceOrderID !== workOrderId) {
    setError(new Error("getLeakageCheckFormAsync: serviceOrderID != workOrder.id"))
    return null
  }

  // dayjs-backed dates the form inputs bind to
  Object.defineProperty(result, "leakageCheckDateObject", {
    get: function () {
      const d = dayjs(this.leakageCheckDate)
      return !d.isValid() || d.year() < 2000 ? undefined : d
    },
  })
  Object.defineProperty(result, "leakageCheckDateForInput", {
    get: function () {
      return this.leakageCheckDateObject?.format(DATETIME_FORMAT)
    },
    set: function (v) {
      this.leakageCheckDate = v
    },
  })

  Object.defineProperty(result, "dateSignedObject", {
    get: function () {
      const d = dayjs(this.dateSigned)
      return !d.isValid() || d.year() < 2000 ? undefined : d
    },
  })
  Object.defineProperty(result, "dateSignedForInput", {
    get: function () {
      return this.dateSignedObject?.format(DATE_FORMAT)
    },
    set: function (v) {
      this.dateSigned = v
    },
  })
  return result
}

const changePauseAsync = async (fetchAuthAsync, stop = false) => {
  let result = null
  if (typeof fetchAuthAsync !== "function") {
    console.error("changePauseAsync fetchAuthAsync should be a function")
    return result
  }
  startLoading("changePauseAsync")
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/WorkPause`, stop ? "PATCH" : "POST")
    if (raw?.status !== "ok") {
      throw raw
    }
    result = raw.result
  } catch (e) {
    setError(e)
  }
  stopLoading("changePauseAsync")
  return result
}

export const saveChecklistAsync = async (fetchAuthAsync, lcf, isSystem = false) => {
  let result = false
  if (typeof fetchAuthAsync !== "function") {
    console.error("saveChecklistAsync fetchAuthAsync should be a function")
    return result
  }
  startLoading("saveChecklistAsync")
  try {
    clearNotifications()
    const raw = await fetchAuthAsync(
      `${AZURE_URI}/${isSystem ? "Checklist" : "CalibrationReport"}`,
      lcf.id === EMPTY_GUID ? "POST" : "PUT",
      lcf,
    )
    if (raw?.status !== "ok") {
      throw raw
    }
    setSuccess({ message: "Shranjeno" })
    result = true
  } catch (e) {
    setError(e)
  }
  stopLoading("saveChecklistAsync")
  return result
}

const listRecentServiceOrdersAsync = async (fetchAuthAsync, facilityId) => {
  if (facilityId?.length !== 36) {
    console.error("listRecentServiceOrdersAsync: facilityId should be GUID")
    return { orders: [], total: 0 }
  }
  const result = await listPagedEntitesAsync(
    fetchAuthAsync,
    "WorkOrders/Service",
    { pageNum: 1, itemsPerPage: 15 },
    "Completed,Finalized",
    `&facilityId=${facilityId}`,
  )
  return { orders: result.entities, total: result.numberOfRecords }
}

const listServiceOrderDevicesAsync = async (fetchAuthAsync, id) => {
  // a new order has no devices yet, and /ServiceOrder/Devices/{id:guid} answers 404 for "new"
  // (useWorkOrder does load new orders with "new") - do not drop this guard
  if (!id || id === "new") return []
  const result = await listEntitiesAsync(fetchAuthAsync, `ServiceOrder/Devices/${id}`, "?includeAttributes=false")
  return result ?? []
}

const saveWorkOrderTimesAsync = async (fetchAuthAsync, timeEntry, workOrderType, workOrderId) => {
  let result = false
  if (typeof fetchAuthAsync !== "function") {
    console.error("saveWorkOrderTimesAsync fetchAuthAsync should be a function")
    return result
  }
  if (workOrderId?.length !== 36) {
    console.error("workOrderId should be GUID")
    return result
  }
  if (workOrderType?.length < 5) {
    console.error("workOrderType missing")
    return result
  }
  startLoading("saveWorkOrderTimesAsync")
  try {
    const raw = await fetchAuthAsync(
      `${AZURE_URI}/WorkOrderTime/${workOrderType}/${workOrderId}`,
      "POST",
      new Array(timeEntry),
    )
    if (raw?.status !== "ok") {
      throw raw
    }
    result = raw?.affectedRows > 0
  } catch (e) {
    setError(e)
  }
  stopLoading("saveWorkOrderTimesAsync")
  return result
}

const saveWorkOrderTransportAsync = async (fetchAuthAsync, transportEntry, workOrderType, workOrderId) => {
  let result = false
  if (typeof fetchAuthAsync !== "function") {
    console.error("saveWorkOrderTransportAsync fetchAuthAsync should be a function")
    return result
  }
  if (workOrderId?.length !== 36) {
    console.error("workOrderId should be GUID")
    return result
  }
  if (workOrderType?.length < 5) {
    console.error("workOrderType missing")
    return result
  }
  startLoading("saveWorkOrderTransportAsync")
  try {
    const raw = await fetchAuthAsync(
      `${AZURE_URI}/WorkOrderTransport/${workOrderType}/${workOrderId}`,
      "POST",
      new Array(transportEntry),
    )
    if (raw?.status !== "ok") {
      throw raw
    }
    result = raw?.affectedRows > 0
  } catch (e) {
    setError(e)
  }
  stopLoading("saveWorkOrderTransportAsync")
  return result
}

const insertNewTransportEntryAsync = async (
  fetchAuthAsync,
  workOrderType,
  workOrderId,
  warehouseId,
  location,
  startTimeObject,
) => {
  let te = null
  if (typeof fetchAuthAsync !== "function") {
    console.error("insertNewTransportEntryAsync fetchAuthAsync should be a function")
    return te
  }
  if (location && (!location.lat || !location.lng)) {
    console.error(location)
    setError("Slaba lokacija.")
    return
  }
  if (!workOrderId) {
    setError("Interna napaka.")
    return
  }
  if (warehouseId?.length !== 36) {
    setError("Interna napaka.")
    console.error("warehouseId should be GUID")
    return
  }
  startLoading("insertNewTransportEntryAsync")
  try {
    const raw1 = await fetchAuthAsync(`${AZURE_URI}/WorkOrderTransport/new`, "GET")
    if (raw1?.status !== "ok") {
      throw raw1
    }
    te = raw1.result
    te.vehicleId = warehouseId
    te.startDateTime = dayjs.isDayjs(startTimeObject) ? startTimeObject.toISOString() : dayjs().toISOString()
    if (location) {
      te.startLat = location.lat
      te.startLng = location.lng
      te.hasStartLocation = true
    } else {
      te.hasStartLocation = false
    }
    te.hasEndLocation = false
    te.hasStartEndLocation = false
    const raw = await fetchAuthAsync(
      `${AZURE_URI}/WorkOrderTransport/${workOrderType}/${workOrderId}`,
      "POST",
      new Array(te),
    )
    if (raw?.status !== "ok") {
      throw raw
    }
    ensureTransportEntryExtensions(te)
    te.isNew = false
  } catch (e) {
    setError(e)
    te = null
  }
  stopLoading("insertNewTransportEntryAsync")
  return te
}

const insertNewTimeEntryAsync = async (fetchAuthAsync, workOrderType, workOrderId, startTimeObject) => {
  let te = null
  if (typeof fetchAuthAsync !== "function") {
    console.error("insertNewTimeEntryAsync fetchAuthAsync should be a function")
    return te
  }
  if (!workOrderId) {
    setError("Interna napaka.")
    return
  }
  if (!dayjs.isDayjs(startTimeObject)) {
    setError("Interna napaka.")
    console.error("startTimeObject should be dayjs object")
    return
  }
  startLoading("insertNewTimeEntryAsync")
  try {
    const raw1 = await fetchAuthAsync(`${AZURE_URI}/WorkOrderTime/new`, "GET")
    if (raw1?.status !== "ok") {
      throw raw1
    }
    te = raw1.result
    te.startDateTime = startTimeObject.toISOString()
    const raw = await fetchAuthAsync(
      `${AZURE_URI}/WorkOrderTime/${workOrderType}/${workOrderId}`,
      "POST",
      new Array(te),
    )
    if (raw?.status !== "ok") {
      throw raw
    }
    ensureTimeEntryExtensions(te)
  } catch (e) {
    setError(e)
  }
  stopLoading("insertNewTimeEntryAsync")
  te.isNew = false
  return te
}

export const getVehicleStopsAsync = async (fetchAuthAsync, vehicleRegNumber, from, to) => {
  let result = []
  if (typeof fetchAuthAsync !== "function") {
    console.error("getVehicleStopsAsync fetchAuthAsync should be a function")
    return result
  }
  if (!vehicleRegNumber) {
    return result
  }
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/VehicleStops/${vehicleRegNumber}?from=${from}&to=${to}`, "GET")
    if (raw?.status !== "ok") {
      throw raw
    }
    result = raw.result
  } catch (e) {
    console.error("getVehicleStopsAsync error", e)
  }
  return result ? result : []
}

const changeWarehouseAsync = async (fetchAuthAsync, warehouseId) => {
  let result = null
  startLoading("changeWarehouseAsync")
  try {
    clearNotifications()
    const raw = await fetchAuthAsync(`${AZURE_URI}/SelectedWarehouse`, "POST", { warehouseId })
    if (raw?.status !== "ok") {
      throw raw
    }
    result = true
    setSuccess({ message: "Skladišče določeno" })
  } catch (e) {
    setError(e)
  }
  stopLoading("changeWarehouseAsync")
  return result
}

const saveWarehouseTransferAsync = async (fetchAuthAsync, warehouseTransfer, isNew, extraParams = "") => {
  let result = null
  if (typeof fetchAuthAsync !== "function") {
    console.error("saveWarehouseTransferAsync fetchAuthAsync should be a function")
    return result
  }
  startLoading("saveWarehouseTransferAsync")
  try {
    clearNotifications()
    const raw = await fetchAuthAsync(
      `${AZURE_URI}/WarehouseTransfer${extraParams}`,
      isNew ? "POST" : "PUT",
      warehouseTransfer,
    )
    if (raw?.status !== "ok") {
      throw raw
    }
    if (raw.id) {
      warehouseTransfer.id = raw.id
    }
    result = true
    setSuccess({ message: warehouseTransfer.status == 2 ? "Zaključeno" : "Shranjeno" })
  } catch (e) {
    setError(e)
  }
  stopLoading("saveWarehouseTransferAsync")
  return result
}

const addDateProperty = (obj, name) => {
  Object.defineProperty(obj, name + "Object", {
    get: function () {
      const d = dayjs.utc(this[name]) // warning: year 1 is covnerted to 2001
      return !d.isValid() || d.year() < 2002 ? undefined : d
    },
  })
}

const toNullWhen0001 = (obj, name) => {
  if (obj[name].startsWith("0001")) {
    obj[name] = ""
  }
}

const getDeviceAsync = async (fetchAuthAsync, id) => {
  const result = await getEntityAsync(fetchAuthAsync, "Device", id)
  if (!result) return null
  // unset date columns come back as "0001-01-01...", the views read dayjs objects
  try {
    result.warrantyValidUntil = result.warrantyValidUntil?.substring(0, 10)
    toNullWhen0001(result, "lastLeakageTest")
    toNullWhen0001(result, "lastService")
    toNullWhen0001(result, "lastCondenserCleaning")
    toNullWhen0001(result, "lastCO2Sensor")
    toNullWhen0001(result, "lastThermometerCalibration")
    addDateProperty(result, "lastLeakageTest")
    addDateProperty(result, "lastService")
    addDateProperty(result, "lastCondenserCleaning")
    addDateProperty(result, "lastCO2Sensor")
    addDateProperty(result, "lastThermometerCalibration")
  } catch (e) {
    setError(e)
  }
  return result
}

const saveEntityAsync = async (fetchAuthAsync, path, entity, isNew, successMessage = "Shranjeno.") => {
  let result = null
  if (typeof fetchAuthAsync !== "function") {
    console.error("saveEntityAsync fetchAuthAsync should be a function")
    return result
  }
  startLoading("saveEntityAsync")
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/${path}`, isNew ? "POST" : "PUT", entity)
    if (raw?.status !== "ok") {
      throw raw
    }
    result = true
    setSuccess(successMessage)
  } catch (e) {
    setError(e)
  }
  stopLoading("saveEntityAsync")
  return result
}

const getCallAsync = async (fetchAuthAsync, id) => {
  if (!id || id === "new") return null
  // the same endpoint as the list, just for one id - the result is always an array
  const calls = await listEntitiesAsync(fetchAuthAsync, `Calls/List/${id}`)
  return calls?.[0] ?? null
}

// The calls list, paged: targetNumber is our number (the XSI-Events subscriber the call came in
// on, an exact match), callerNumber a partial match on the caller's number.
const listPagedCallsAsync = async (fetchAuthAsync, paging, filter = "") => {
  let result = { entities: [], numberOfRecords: 0 }
  if (typeof fetchAuthAsync !== "function") {
    console.error("listPagedCallsAsync fetchAuthAsync should be a function")
    return result
  }
  if (!paging?.pageNum || !paging?.itemsPerPage) {
    console.error("listPagedCallsAsync paging missing required.")
    return result
  }
  startLoading("listPagedCallsAsync")
  try {
    const raw = await fetchAuthAsync(
      `${AZURE_URI}/Calls/List?pageNum=${paging.pageNum}&itemsPerPage=${paging.itemsPerPage}${filter}`,
      "GET",
    )
    if (!raw?.itemsPerPage) {
      throw raw
    }
    result.entities = raw.result
    result.numberOfRecords = raw.numberOfRecords
  } catch (e) {
    setError(e)
  }
  stopLoading("listPagedCallsAsync")
  return result
}

// What the call was about; the backend creates/links the resulting order (see CallSetOutcome).
const setCallOutcomeAsync = async (fetchAuthAsync, callId, outcome, serviceOrderID = "") => {
  let result = null
  if (typeof fetchAuthAsync !== "function") {
    console.error("setCallOutcomeAsync fetchAuthAsync should be a function")
    return result
  }
  if (callId?.length !== 36) {
    console.error("setCallOutcomeAsync callId should be GUID")
    return result
  }
  if (!outcome) {
    console.error("setCallOutcomeAsync outcome missing")
    return result
  }
  startLoading("setCallOutcomeAsync")
  try {
    const body = { outcome }
    if (serviceOrderID) {
      body.serviceOrderID = serviceOrderID
    }
    const raw = await fetchAuthAsync(`${AZURE_URI}/Calls/${callId}/Outcome`, "POST", body)
    if (raw?.status !== "ok") {
      throw raw
    }
    result = raw
  } catch (e) {
    setError(e)
  }
  stopLoading("setCallOutcomeAsync")
  return result
}

const unlinkCallOutcomeAsync = async (fetchAuthAsync, callId) => {
  let result = null
  if (typeof fetchAuthAsync !== "function") {
    console.error("unlinkCallOutcomeAsync fetchAuthAsync should be a function")
    return result
  }
  if (callId?.length !== 36) {
    console.error("unlinkCallOutcomeAsync callId should be GUID")
    return result
  }
  startLoading("unlinkCallOutcomeAsync")
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/Calls/${callId}/Outcome`, "DELETE")
    if (raw?.status !== "ok") {
      throw raw
    }
    result = raw
  } catch (e) {
    setError(e)
  }
  stopLoading("unlinkCallOutcomeAsync")
  return result
}

const setDeviceDefrostingAsync = async (fetchAuthAsync, deviceId, isDefrosting) => {
  let result = null
  if (typeof fetchAuthAsync !== "function") {
    console.error("setDeviceDefrostingAsync fetchAuthAsync should be a function")
    return result
  }
  if (deviceId?.length !== 36) {
    console.error("setDeviceDefrostingAsync deviceId should be GUID")
    return result
  }
  startLoading("setDeviceDefrostingAsync")
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/Device/${deviceId}?defrosting=${!!isDefrosting}`, "PATCH")
    if (raw?.status !== "ok") {
      throw raw
    }
    result = raw
  } catch (e) {
    setError(e)
  }
  stopLoading("setDeviceDefrostingAsync")
  return result
}

const saveFacilityContactAsync = async (fetchAuthAsync, facilityContact, facility) => {
  startLoading("saveFacilityContactAsync")
  if (typeof fetchAuthAsync !== "function") {
    console.error("saveFacilityContactAsync fetchAuthAsync should be a function")
    return
  }
  if (!facilityContact) {
    setError("Kontakt je obvezen")
    return
  }
  if (!facility || !facility.id) {
    console.warn(facility)
    setError("Napaka")
    return
  }

  try {
    facilityContact.facilityID = facility.id

    const raw = await fetchAuthAsync(`${AZURE_URI}/FacilityContact`, "POST", facilityContact)
    if (raw?.status !== "ok") {
      throw raw
    }
    setSuccess({ message: "Nov podpisnik shranjen" })
  } catch (e) {
    setError(e)
  }
  stopLoading("saveFacilityContactAsync")
}

export const saveWaypointsAsync = async (fetchAuthAsync, workerId, day, waypoints) => {
  let result = false
  startLoading("saveWaypointsAsync")
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/Waypoints/${workerId}/${day}`, "POST", waypoints)
    if (raw?.status !== "ok") {
      throw raw
    }
    result = true
  } catch (e) {
    setError(e)
  }
  stopLoading("saveWaypointsAsync")
  return result
}

export const sendWorkerDepartureEmailsAsync = async (fetchAuthAsync, workerId, day) => {
  startLoading("sendWorkerDepartureEmailsAsync")
  try {
    const url = `${AZURE_URI}/WorkerDepartureEmails/${workerId}${day ? `?day=${day}` : ""}`
    return await fetchAuthAsync(url, "POST")
  } catch (e) {
    setError(e)
    return null
  } finally {
    stopLoading("sendWorkerDepartureEmailsAsync")
  }
}

export const assignOrderAsync = async (fetchAuthAsync, workerId, orderId) => {
  // are we duplicating patchWorkOrderAsync ?
  let success = false
  if (!workerId || !orderId) {
    console.error(`WorkerId or OrderId is missing`)
    return success
  }
  startLoading(`changeStatus`)
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/WorkOrder/Service/${orderId}`, "PATCH", {
      newOwnerId: workerId,
      status: "Accepted",
    })
    if (raw?.status === "warn") {
      setWarning(raw.message ?? `Nalog ni bil dodeljen (${raw.result}).`)
    } else if (raw?.status !== "ok") {
      throw raw
    } else if (raw.result) {
      setSuccess(`Naloga dodeljena`)
      success = true
    }
  } catch (e) {
    setError(e)
  }
  stopLoading(`changeStatus`)
  return success
}

export {
  saveEntityAsync,
  saveFacilityContactAsync,
  listEntitiesAsync,
  listPagedEntitesAsync,
  getWorkOrderAsync,
  patchWorkOrderAsync,
  getFacilityAsync,
  searchDevicesAsync,
  searchMaterialsAsync,
  getEntityAsync,
  getLeakageCheckFormAsync,
  listRecentServiceOrdersAsync,
  saveWorkOrderTransportAsync,
  insertNewTransportEntryAsync,
  changePauseAsync,
  saveWorkOrderTimesAsync,
  insertNewTimeEntryAsync,
  deleteFacilityContactAsync,
  saveMaterialsAsync,
  saveWarehouseTransferAsync,
  getDeviceAsync,
  listServiceOrderDevicesAsync,
  changeWarehouseAsync,
  getCallAsync,
  listPagedCallsAsync,
  setCallOutcomeAsync,
  unlinkCallOutcomeAsync,
  setDeviceDefrostingAsync,
}
